import { useState, type FC } from "react";
import type { ClientModel } from "../../models/ClientModel";
import type { InvoiceUploadUnitModel } from "../../models/InvoiceUploadUnitModel";
import styled from "styled-components";
import { theme } from "../../theme";
import Subheading from "../atoms/typography/Subheading";
import BodySmall from "../atoms/typography/BodySmall";
import DetailsButton from "../atoms/buttons/DetailsButton";
import { OpenLinkIconSmall } from "../atoms/icons/OpenLinkIcon";
import ChevronButton from "../atoms/buttons/ChevronButtons";
import { useNavigate } from "react-router-dom";
import UpdateEmailConnectionsButton from "../molecules/documentHandler/UpdateEmailConnectionsButton";
import { useTranslation } from "react-i18next";
import ClientUploadUnitExpanded from "./documentHandler/ClientUploadUnitExpanded";
import type { InvoiceUploadUnitStatus } from "../../models/InvoiceUploadUnitStatus";
import { DONE_STATUSES } from "../templates/DocumentHandler";

interface Props {
  totalCount: number;
  client?: ClientModel;
  onToggleDocument: (documentId: string, clientIdentifier?: string) => void;
  checkIfDocumentIsSelected: (documentId: string) => boolean;
  checkIfAllDocumentsInUploadUnitIsSelected: (
    uploadUnit: InvoiceUploadUnitModel,
  ) => boolean;
  onToggleUploadUnit: (
    uploadUnit: InvoiceUploadUnitModel,
    clientIdentifier?: string,
  ) => void;
  selectedDocuments: Array<string>;
  selectedClientId?: string;
  onClearSelection: () => void;
  statuses: Array<InvoiceUploadUnitStatus>;
  sortDirection: "ASC" | "DESC";
}

const SectionOutline = styled.div<{ isExpanded: boolean }>`
  border-bottom: 2px solid ${theme.colors.grey05};
  border-left: ${(props) =>
    props.isExpanded ? `1px solid ${theme.colors.grey05}` : "none"};
  border-right: ${(props) =>
    props.isExpanded ? `1px solid ${theme.colors.grey05}` : "none"};
`;

const Header = styled.div<{ $isExpanded?: boolean }>`
  padding: 12px 16px;
  background-color: ${({ $isExpanded }) =>
    $isExpanded ? theme.colors.hover : theme.colors.white};
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const FlexedDiv = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const ClientUploadUnit: FC<Props> = ({
  totalCount,
  client,
  onToggleDocument,
  onToggleUploadUnit,
  checkIfAllDocumentsInUploadUnitIsSelected,
  checkIfDocumentIsSelected,
  selectedDocuments,
  selectedClientId,
  onClearSelection,
  statuses,
  sortDirection,
}) => {
  const { t } = useTranslation();
  const naviagte = useNavigate();

  const [clientIsExpanded, setClientIsExpanded] = useState<boolean>(false);

  //if on registered tab, fetch maximum 5 units per client
  const maxUnitsPerClient = statuses === DONE_STATUSES ? 5 : undefined;

  //Action functions
  const onClickGoToClientCard = (
    event: React.MouseEvent,
    clientIdentifier: string,
  ) => {
    event.stopPropagation();
    naviagte(`/client/${clientIdentifier}`);
  };

  return (
    <SectionOutline isExpanded={clientIsExpanded}>
      <Header
        $isExpanded={clientIsExpanded}
        onClick={() => setClientIsExpanded(!clientIsExpanded)}
      >
        <div>
          <Subheading color={theme.colors.primary}>
            {client?.name ?? t("documentHandler.missingConnection")} (
            {totalCount})
          </Subheading>
          <BodySmall color={theme.colors.textSecondary}>{client?.id}</BodySmall>
        </div>
        <FlexedDiv>
          {client?.id && (
            <DetailsButton
              title={t("documentHandler.goToClientCard")}
              color={theme.colors.primary}
              onClick={(e?: React.MouseEvent) =>
                onClickGoToClientCard(e!, client?.id)
              }
              icon={<OpenLinkIconSmall color={theme.colors.primary} />}
            />
          )}
          {client &&
            client.emailConnections &&
            client.emailConnections?.length > 0 && (
              <UpdateEmailConnectionsButton
                emailConnections={client.emailConnections}
              />
            )}
          {clientIsExpanded ? (
            <ChevronButton
              type="up"
              onClick={() => setClientIsExpanded(false)}
            />
          ) : (
            <ChevronButton
              type="down"
              onClick={() => setClientIsExpanded(true)}
            />
          )}
        </FlexedDiv>
      </Header>
      {clientIsExpanded && (
        <ClientUploadUnitExpanded
          clientIdentifier={client?.id}
          missingClientIdentifier={!client}
          sortDirection={sortDirection}
          onClearSelection={onClearSelection}
          statuses={statuses}
          onToggleDocument={onToggleDocument}
          onToggleUploadUnit={onToggleUploadUnit}
          checkIfAllDocumentsInUploadUnitIsSelected={
            checkIfAllDocumentsInUploadUnitIsSelected
          }
          checkIfDocumentIsSelected={checkIfDocumentIsSelected}
          selectedDocuments={selectedDocuments}
          selectedClientId={selectedClientId}
          maxUnitsPerClient={maxUnitsPerClient}
          totalCount={totalCount}
        />
      )}
    </SectionOutline>
  );
};

export default ClientUploadUnit;
